import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, ChevronDown } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { openWhatsApp } from "@/lib/whatsapp";
import { PRIMARY_NAV, AUDIENCE_NAV, PAGES } from "@/lib/nav";

const LOGO = "/tytocare-logo.png";

export default function Header() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const isActive = (path) => pathname === path;

  return (
    <motion.header
      initial={{ y: -90, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/80 border-b border-[#5B2C91]/10"
      data-testid="site-header"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 lg:h-20 items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="header-logo-link">
            <img src={LOGO} alt="TytoCare" className="h-6 lg:h-8 w-auto" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8" data-testid="header-nav">
            <NavLink to={PRIMARY_NAV[0].path} label={PRIMARY_NAV[0].label} active={isActive(PRIMARY_NAV[0].path)} id="come-funziona" />
            <NavLink to={PRIMARY_NAV[1].path} label={PRIMARY_NAV[1].label} active={isActive(PRIMARY_NAV[1].path)} id="medici" />

            {/* Per chi è dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="inline-flex items-center gap-1 text-sm font-medium text-[#4A4059] hover:text-[#5B2C91] transition-colors duration-200 outline-none"
                  data-testid="nav-per-chi-e"
                >
                  Per chi è
                  <ChevronDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52 rounded-2xl p-2">
                {AUDIENCE_NAV.map((item) => (
                  <DropdownMenuItem key={item.path} asChild className="rounded-xl cursor-pointer">
                    <Link to={item.path} data-testid={`nav-dd-${item.path.slice(1)}`} className="text-sm font-medium text-[#1F152E]">
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <NavLink to={PRIMARY_NAV[2].path} label={PRIMARY_NAV[2].label} active={isActive(PRIMARY_NAV[2].path)} id="prezzi" />
            <NavLink to={PRIMARY_NAV[3].path} label={PRIMARY_NAV[3].label} active={isActive(PRIMARY_NAV[3].path)} id="rsa-strutture" />
            <NavLink to="/lavora-con-noi" label="Lavora con noi" active={isActive("/lavora-con-noi")} id="lavora-con-noi" />
            <NavLink to="/contatti" label="Contatti" active={isActive("/contatti")} id="contatti" />
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => openWhatsApp()}
              className="hidden md:inline-flex items-center gap-2 rounded-full bg-[#5B2C91] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(91,44,145,0.28)] hover:bg-[#4A2277] hover:-translate-y-0.5 transition-[transform,background-color] duration-200"
              data-testid="header-whatsapp-btn"
            >
              <FaWhatsapp className="h-4 w-4" />
              Richiedi una visita
            </button>

            <button
              onClick={() => openWhatsApp()}
              aria-label="Richiedi una visita su WhatsApp"
              className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_8px_18px_rgba(37,211,102,0.35)]"
              data-testid="header-whatsapp-compact"
            >
              <FaWhatsapp className="h-5 w-5" />
            </button>

            {/* Hamburger */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  aria-label="Apri menu"
                  className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#5B2C91]/15 text-[#5B2C91] hover:bg-[#F6F4FA] transition-colors"
                  data-testid="header-hamburger"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[86%] sm:max-w-sm overflow-y-auto bg-white border-l border-[#5B2C91]/10">
                <Link to="/" onClick={() => setOpen(false)} className="mb-8 mt-1 flex items-center gap-2">
                  <img src={LOGO} alt="TytoCare" className="h-7 w-auto" />
                </Link>
                <nav className="flex flex-col gap-1">
                  {PAGES.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setOpen(false)}
                      className={`rounded-2xl px-4 py-3 text-base font-medium transition-colors ${
                        isActive(item.path) ? "bg-[#5B2C91]/10 text-[#5B2C91]" : "text-[#1F152E] hover:bg-[#F6F4FA]"
                      }`}
                      data-testid={`mobile-nav-${item.path.slice(1)}`}
                    >
                      {item.label}
                    </Link>
                  ))}
                </nav>
                <button
                  onClick={() => {
                    openWhatsApp();
                    setOpen(false);
                  }}
                  className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#5B2C91] px-5 py-4 text-base font-semibold text-white"
                  data-testid="mobile-whatsapp-btn"
                >
                  <FaWhatsapp className="h-5 w-5" />
                  Richiedi una visita
                </button>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </motion.header>
  );
}

function NavLink({ to, label, active, id }) {
  return (
    <Link
      to={to}
      className={`relative text-sm font-medium transition-colors duration-200 after:absolute after:-bottom-1 after:left-0 after:h-0.5 after:bg-[#5B2C91] after:transition-all after:duration-300 ${
        active ? "text-[#5B2C91] after:w-full" : "text-[#4A4059] hover:text-[#5B2C91] after:w-0 hover:after:w-full"
      }`}
      data-testid={`nav-${id}`}
    >
      {label}
    </Link>
  );
}
